import { useCallback, useMemo, useState } from "react";
import { DEFAULT_ORIGIN_ZIP_CODE, PRODUCTS } from "../constants/products";
import {
  ClientData,
  DiscountType,
  FreightData,
  ProposalDraft,
  ProposalProduct,
  ProposalTotals,
} from "../types/proposal";
import { formatPhone, formatZipCode } from "../utils/masks";
import { createEmptyAddress } from "../utils/proposalOperationalDetails";
import {
  buildProposalProducts,
  calculateProposal,
} from "../utils/proposalCalculations";
import { generateWhatsappMessage } from "../utils/whatsappMessage";

const initialClient: ClientData = {
  name: "",
  company: "",
  email: "",
  phone: "",
  zipCode: "",
  document: "",
  source: "manual",
  userId: "",
  asaasCustomerId: "",
  address: createEmptyAddress(),
};

const initialFreight: FreightData = {
  originZipCode: DEFAULT_ORIGIN_ZIP_CODE,
  destinationZipCode: "",
  value: 0,
};

export type UseProposalReturn = {
  client: ClientData;
  products: ProposalProduct[];
  freight: FreightData;
  discountType: DiscountType;
  discountValue: number;
  totals: ProposalTotals;
  message: string;
  draft: ProposalDraft;
  updateClient: <K extends keyof ClientData>(
    field: K,
    value: ClientData[K],
  ) => void;
  patchClient: (data: Partial<ClientData>) => void;
  toggleProduct: (productId: string) => void;
  updateProductQuantity: (productId: string, quantity: number) => void;
  updateFreight: <K extends keyof FreightData>(
    field: K,
    value: FreightData[K],
  ) => void;
  setDiscountType: (type: DiscountType) => void;
  setDiscountValue: (value: number) => void;
  resetProposal: () => void;
  loadProposal: (draft: ProposalDraft) => void;
};

export const useProposal = (): UseProposalReturn => {
  const [client, setClient] = useState<ClientData>(initialClient);
  const [products, setProducts] = useState<ProposalProduct[]>(() =>
    buildProposalProducts(PRODUCTS),
  );
  const [freight, setFreight] = useState<FreightData>(initialFreight);
  const [discountType, setDiscountTypeState] = useState<DiscountType>("none");
  const [discountValue, setDiscountValueState] = useState(0);

  const updateClient = useCallback(
    <K extends keyof ClientData>(field: K, value: ClientData[K]) => {
      let formattedValue = value;
      if (field === "phone")
        formattedValue = formatPhone(String(value)) as ClientData[K];
      if (field === "zipCode")
        formattedValue = formatZipCode(String(value)) as ClientData[K];

      setClient(current => ({ ...current, [field]: formattedValue }));
      if (field === "zipCode") {
        setFreight(current => ({
          ...current,
          destinationZipCode: String(formattedValue),
        }));
      }
    },
    [],
  );

  const patchClient = useCallback((data: Partial<ClientData>) => {
    setClient(current => ({
      ...current,
      ...data,
      phone: data.phone !== undefined ? formatPhone(data.phone) : current.phone,
      zipCode:
        data.zipCode !== undefined
          ? formatZipCode(data.zipCode)
          : data.address?.zipCode
          ? formatZipCode(data.address.zipCode)
          : current.zipCode,
      address: {
        ...createEmptyAddress(),
        ...current.address,
        ...data.address,
      },
    }));

    const destinationZipCode = data.zipCode ?? data.address?.zipCode;
    if (destinationZipCode !== undefined) {
      setFreight(currentFreight => ({
        ...currentFreight,
        destinationZipCode: formatZipCode(destinationZipCode),
      }));
    }
  }, []);

  const toggleProduct = useCallback((productId: string) => {
    setProducts(current =>
      current.map(product =>
        product.id === productId
          ? { ...product, selected: !product.selected }
          : product,
      ),
    );
  }, []);

  const updateProductQuantity = useCallback(
    (productId: string, quantity: number) => {
      setProducts(current =>
        current.map(product =>
          product.id === productId
            ? { ...product, quantity: Math.max(1, Math.floor(quantity || 1)) }
            : product,
        ),
      );
    },
    [],
  );

  const updateFreight = useCallback(
    <K extends keyof FreightData>(field: K, value: FreightData[K]) => {
      const formattedValue = field.toLowerCase().includes("zipcode")
        ? formatZipCode(String(value))
        : Math.max(0, Number(value) || 0);
      setFreight(current => ({ ...current, [field]: formattedValue }));
    },
    [],
  );

  const setDiscountType = useCallback((type: DiscountType) => {
    setDiscountTypeState(type);
    if (type === "none") setDiscountValueState(0);
  }, []);

  const setDiscountValue = useCallback(
    (value: number) => {
      const normalizedValue = Math.max(0, Number(value) || 0);
      setDiscountValueState(
        discountType === "percentage"
          ? Math.min(normalizedValue, 100)
          : normalizedValue,
      );
    },
    [discountType],
  );

  const calculation = useMemo(
    () =>
      calculateProposal(products, freight.value, discountType, discountValue),
    [discountType, discountValue, freight.value, products],
  );

  const message = useMemo(
    () =>
      generateWhatsappMessage({
        client,
        products: calculation.products,
        totals: calculation.totals,
      }),
    [calculation, client],
  );

  const draft = useMemo<ProposalDraft>(
    () => ({
      client: { ...client },
      products: calculation.products.map(product => ({ ...product })),
      freight: { ...freight },
      discountType,
      discountValue,
    }),
    [calculation.products, client, discountType, discountValue, freight],
  );

  const resetProposal = useCallback(() => {
    setClient({ ...initialClient, address: createEmptyAddress() });
    setProducts(buildProposalProducts(PRODUCTS));
    setFreight(initialFreight);
    setDiscountTypeState("none");
    setDiscountValueState(0);
  }, []);

  const loadProposal = useCallback((savedDraft: ProposalDraft) => {
    setClient({
      ...initialClient,
      ...savedDraft.client,
      address: {
        ...createEmptyAddress(),
        ...savedDraft.client.address,
        zipCode:
          savedDraft.client.address?.zipCode ?? savedDraft.client.zipCode,
      },
    });
    setProducts(savedDraft.products.map(product => ({ ...product })));
    setFreight({ ...savedDraft.freight });
    setDiscountTypeState(savedDraft.discountType);
    setDiscountValueState(savedDraft.discountValue);
  }, []);

  return {
    client,
    products: calculation.products,
    freight,
    discountType,
    discountValue,
    totals: calculation.totals,
    message,
    draft,
    updateClient,
    patchClient,
    toggleProduct,
    updateProductQuantity,
    updateFreight,
    setDiscountType,
    setDiscountValue,
    resetProposal,
    loadProposal,
  };
};
